import { useEffect, useRef, useCallback, useState } from 'react';

// ─── Global sound preference ─────────────────────────────────────────────
// Persisted in localStorage so the user's mute choice survives reloads.
// Pages call `useSoundsMuted()` to read or toggle the flag; the games and
// the navbar mute button both read this single source of truth.
const MUTED_KEY = 'ikz_sounds_muted';
const listeners = new Set();

function readMuted() {
  try { return localStorage.getItem(MUTED_KEY) === '1'; } catch { return false; }
}
function writeMuted(val) {
  try { localStorage.setItem(MUTED_KEY, val ? '1' : '0'); } catch { /* noop */ }
  listeners.forEach(l => l(val));
}

export function useSoundsMuted() {
  const [muted, setMutedState] = useState(readMuted);
  useEffect(() => {
    const sub = (v) => setMutedState(prev => (prev === v ? prev : v));
    listeners.add(sub);
    return () => { listeners.delete(sub); };
  }, []);
  const setMuted = useCallback((val) => {
    writeMuted(!!val);
  }, []);
  const toggle = useCallback(() => writeMuted(!readMuted()), []);
  return { muted, setMuted, toggle };
}

// ─── Sound playback ─────────────────────────────────────────────────────
// Built on the WebAudio API so we don't need to ship any audio files. Each
// sound is a short synthesised tone (or a tiny envelope of tones) shaped
// to fit the gameplay event:
//   correct  → quick rising chime
//   wrong    → short low buzz
//   tick     → soft 1kHz tick (used for last-3-seconds countdown)
//   start    → ascending two-note "go" cue
//   win      → bright three-note arpeggio
//   lose     → descending two-note "done" cue
//   click    → percussive UI click
//
// We share ONE AudioContext across the app — most browsers limit total
// AudioContexts so this is the most reliable approach. Calls before the
// user has interacted with the page are no-ops (autoplay policy).
let _ctx = null;
function getCtx() {
  if (typeof window === 'undefined') return null;
  if (_ctx) return _ctx;
  const Ctor = window.AudioContext || window.webkitAudioContext;
  if (!Ctor) return null;
  try { _ctx = new Ctor(); } catch { _ctx = null; }
  return _ctx;
}

function playTone(ctx, freq, durationMs, type = 'sine', gain = 0.15, when = 0) {
  if (!ctx) return;
  try {
    const t0 = ctx.currentTime + when;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    // Smooth attack/decay envelope so the tones never click.
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + durationMs / 1000);
    osc.connect(g).connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + durationMs / 1000 + 0.05);
  } catch { /* noop */ }
}

const RECIPES = {
  correct: (ctx) => {
    playTone(ctx, 660, 90, 'sine', 0.18, 0);
    playTone(ctx, 880, 140, 'sine', 0.2, 0.07);
  },
  wrong: (ctx) => {
    playTone(ctx, 220, 220, 'square', 0.12, 0);
  },
  tick: (ctx) => {
    playTone(ctx, 1000, 50, 'square', 0.07, 0);
  },
  start: (ctx) => {
    playTone(ctx, 523, 110, 'triangle', 0.18, 0);
    playTone(ctx, 783, 160, 'triangle', 0.2, 0.1);
  },
  win: (ctx) => {
    playTone(ctx, 523, 120, 'triangle', 0.18, 0);
    playTone(ctx, 659, 120, 'triangle', 0.2, 0.1);
    playTone(ctx, 880, 220, 'triangle', 0.22, 0.2);
  },
  lose: (ctx) => {
    playTone(ctx, 392, 160, 'sine', 0.16, 0);
    playTone(ctx, 261, 240, 'sine', 0.18, 0.13);
  },
  click: (ctx) => {
    playTone(ctx, 600, 35, 'square', 0.08, 0);
  },
};

// `useGameSounds` returns a stable `play(name)` function. It respects the
// global mute flag and silently bails out before the user interacts (which
// would otherwise trigger an AudioContext warning).
export function useGameSounds() {
  const mutedRef = useRef(readMuted());
  useEffect(() => {
    const sub = (v) => { mutedRef.current = v; };
    listeners.add(sub);
    return () => listeners.delete(sub);
  }, []);
  const play = useCallback((name) => {
    if (mutedRef.current) return;
    const ctx = getCtx();
    if (!ctx) return;
    // Resume context if it's suspended (some browsers start in 'suspended').
    if (ctx.state === 'suspended') {
      try { ctx.resume(); } catch { /* noop */ }
    }
    const recipe = RECIPES[name];
    if (recipe) recipe(ctx);
  }, []);
  return { play };
}
